import type { ReviewResult, SuggestionField } from '@lixionary-jira/domain';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';
import { isBusy, useTicketAssistant, type TicketAssistant } from '../../api/assistant';
import { api } from '../../api/client';
import { useAction } from '../../api/hooks';
import { Badge, type Tone } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Alert, ErrorAlert, Spinner } from '../../ui/feedback';
import { Checkbox } from '../../ui/forms';
import { Markdown } from '../../ui/Markdown';
import { Card } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';
import { ActivityList, AnalysisDetails } from './AssistantDraft';

const VERDICT: Record<ReviewResult['verdict'], { label: string; tone: Tone }> = {
  ready: { label: 'Ready for review', tone: 'success' },
  needs_clarification: { label: 'Needs clarification', tone: 'warning' },
  needs_rework: { label: 'Needs rework', tone: 'error' },
};

const FIELD_LABEL: Record<SuggestionField, string> = {
  title: 'Title',
  description: 'Description',
  acceptanceCriteria: 'Acceptance criteria',
  stepsOrOutcome: 'Steps / intended outcome',
};

/**
 * Assistant verification of the ticket: a background review of form submissions, or the analysis from the
 * conversation that drafted it. Suggestions change nothing until the requester or an admin applies them.
 */
export function AssistantPanel({
  projectKey,
  number,
  ticketVersion,
}: {
  projectKey: string;
  number: number;
  /** Current ticket version from the page, so applying never races a stale copy. */
  ticketVersion: number;
}) {
  const q = useTicketAssistant(projectKey, number);
  const data = q.data;
  const rerun = useAction(
    () => api.post<TicketAssistant>(`/projects/${projectKey}/tickets/${number}/assistant/review`),
    { success: 'Assistant review requested', invalidate: [['ticket-assistant', projectKey, number]] },
  );
  if (!data || (!data.review && !data.origin && !data.canRequestReview)) return null;
  const review = data.review;
  const busy = isBusy(review?.status);
  return (
    <section id="assistant">
      <Card
        title={
          <span className="row nowrap">
            <Sparkles size={18} aria-hidden /> Assistant review
          </span>
        }
        subtitle={
          review
            ? `${review.displayId}${review.model ? ` · ${review.model}` : ''}${review.repositoryRevision ? ` · commit ${review.repositoryRevision.slice(0, 10)}` : ''}`
            : data.origin
              ? `Drafted with the assistant in ${data.origin.displayId}`
              : 'Check this request against the project code'
        }
        action={
          data.canRequestReview && !busy ? (
            <Button
              size="sm"
              variant="secondary"
              loading={rerun.isPending}
              onClick={() => rerun.mutate(undefined)}
            >
              {review ? 'Review again' : 'Review with assistant'}
            </Button>
          ) : undefined
        }
      >
        <ErrorAlert error={rerun.error} />
        {busy && review && (
          <div className="stack sm">
            <span className="row nowrap small">
              <Spinner />{' '}
              {review.status === 'queued' ? 'Waiting to start…' : 'Checking the request against the code…'}
            </span>
            {review.activity.length > 0 && <ActivityList activity={review.activity.slice(-6)} />}
          </div>
        )}
        {review?.status === 'failed' && (
          <Alert tone="error" title="The review did not finish">
            {review.error}
          </Alert>
        )}
        {review?.status === 'completed' && review.result && (
          <ReviewBody
            key={review.id}
            data={data}
            ticketVersion={ticketVersion}
            review={review as NonNullable<TicketAssistant['review']> & { result: ReviewResult }}
            projectKey={projectKey}
            number={number}
          />
        )}
        {!review && data.origin?.analysis && (
          <AnalysisDetails
            analysis={data.origin.analysis}
            ticketHref={(n) => `/projects/${projectKey}/tickets/${n}`}
          />
        )}
      </Card>
    </section>
  );
}

function ReviewBody({
  data,
  ticketVersion,
  review,
  projectKey,
  number,
}: {
  data: TicketAssistant;
  ticketVersion: number;
  review: NonNullable<TicketAssistant['review']> & { result: ReviewResult };
  projectKey: string;
  number: number;
}) {
  const r = review.result;
  const available = (Object.keys(FIELD_LABEL) as SuggestionField[]).filter((f) => {
    const v = r.suggested[f];
    return Array.isArray(v) ? v.length > 0 : !!v?.trim();
  });
  const [fields, setFields] = useState<SuggestionField[]>(available);
  const [open, setOpen] = useState(false);
  const apply = useAction(
    () =>
      api.post(`/projects/${projectKey}/tickets/${number}/assistant/apply`, {
        expectedVersion: ticketVersion,
        reviewId: review.id,
        fields,
      }),
    {
      success: 'Suggestions applied as a new scope revision',
      invalidate: [
        ['ticket', projectKey, number],
        ['ticket-assistant', projectKey, number],
      ],
      onSuccess: () => setOpen(false),
    },
  );
  const v = VERDICT[r.verdict];
  return (
    <div className="stack">
      <div className="row">
        <Badge tone={v.tone}>{v.label}</Badge>
        <span className="small muted">
          Quality {r.qualityScore}/5 · <Timestamp value={review.finishedAt ?? review.createdAt} />
        </span>
        {review.stale && <Badge tone="default">Scope changed since this review</Badge>}
      </div>
      <Markdown>{r.summary}</Markdown>
      {r.questions.length > 0 && (
        <div className="stack sm">
          <strong className="small">Questions for the requester</strong>
          <ol className="criteria-list">
            {r.questions.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ol>
          <span className="small muted">Answer in the discussion or edit the scope, then review again.</span>
        </div>
      )}
      {r.missingInformation.length > 0 && (
        <div className="stack sm">
          <strong className="small">Missing information</strong>
          <ul>
            {r.missingInformation.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </div>
      )}
      <AnalysisDetails
        analysis={{ affectedAreas: r.relatedCode, possibleDuplicates: r.possibleDuplicates }}
        ticketHref={(n) => `/projects/${projectKey}/tickets/${n}`}
      />
      {r.implementationNotes && (
        <details>
          <summary className="small">Technical analysis and implementation plan</summary>
          <div className="well" style={{ marginTop: 8 }}>
            <Markdown>{r.implementationNotes}</Markdown>
          </div>
        </details>
      )}
      {available.length > 0 && (
        <details open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
          <summary className="small">
            Suggested improvements ({available.map((f) => FIELD_LABEL[f]).join(', ')})
          </summary>
          <div className="stack" style={{ marginTop: 8 }}>
            {available.map((f) => (
              <div key={f} className="stack sm">
                {data.canApply ? (
                  <Checkbox
                    label={FIELD_LABEL[f]}
                    checked={fields.includes(f)}
                    onChange={(on) => setFields(on ? [...fields, f] : fields.filter((x) => x !== f))}
                  />
                ) : (
                  <strong className="small">{FIELD_LABEL[f]}</strong>
                )}
                <div className="well">
                  <Suggestion value={r.suggested[f]!} />
                </div>
              </div>
            ))}
            <ErrorAlert error={apply.error} />
            {data.canApply ? (
              <div className="form-actions inline">
                <Button
                  loading={apply.isPending}
                  disabled={!fields.length}
                  onClick={() => apply.mutate(undefined)}
                >
                  Apply selected suggestions
                </Button>
              </div>
            ) : (
              <span className="small muted">
                The requester or an admin can apply suggestions while the ticket is Pending.
              </span>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

function Suggestion({ value }: { value: string | string[] }) {
  if (Array.isArray(value))
    return (
      <ol className="criteria-list">
        {value.map((c, i) => (
          <li key={i}>
            <Markdown>{c}</Markdown>
          </li>
        ))}
      </ol>
    );
  return <Markdown>{value}</Markdown>;
}
